import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AuthShell from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";

export default function Login({ adminMode = false }) {
  const [sp] = useSearchParams();
  const role = sp.get("role");
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate(user.role === "admin" ? "/admin" : user.role === "partner" ? "/partner" : "/client", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await login(email, password);
      if (adminMode && u.role !== "admin") { setError("This login is for administrators only."); return; }
      navigate(u.role === "admin" ? "/admin" : u.role === "partner" ? "/partner" : "/client", { replace: true });
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === "string" ? d : "Invalid email or password.");
    } finally { setLoading(false); }
  };

  const title = adminMode ? "Admin Login" : role === "partner" ? "Partner Login" : role === "client" ? "Client Login" : "Sign in";
  const fld = "bg-white/5 border-white/10 text-white";

  return (
    <AuthShell title={title} subtitle="Enter your credentials to continue."
      footer={!adminMode && (<>New referral partner? <Link to="/partner/register" className="text-cyan-300 hover:underline">Create an account</Link></>)}>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        {error && <div data-testid="login-error" className="text-sm text-red-300 bg-red-500/10 rounded-lg px-3 py-2">{error}</div>}
        <div><Label className="text-slate-200 mb-1.5 block">Email</Label><Input data-testid="login-email" type="email" required className={fld} value={email} onChange={(e) => setEmail(e.target.value)} /></div>
        <div><Label className="text-slate-200 mb-1.5 block">Password</Label><Input data-testid="login-password" type="password" required className={fld} value={password} onChange={(e) => setPassword(e.target.value)} /></div>
        <div className="text-right"><Link to="/forgot-password" className="text-xs text-slate-400 hover:text-cyan-300">Forgot password?</Link></div>
        <Button type="submit" data-testid={adminMode ? "admin-login-submit" : "login-submit"} disabled={loading} className="w-full h-11 bg-violet-600 hover:bg-violet-500 text-white font-semibold">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
        </Button>
      </form>
    </AuthShell>
  );
}
